import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Bed, Bath, Square, ArrowLeft, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { fixImageUrl, fixImageUrls } from '../utils/imageUtils';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PropertyDetailPage() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/properties/${id}`);
        // Fix image URLs for proper routing
        const data = response.data;
        data.images = fixImageUrls(data.images);
        setProperty(data);
      } catch (error) {
        toast.error('Bien non trouvé');
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    if (property && property.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
    }
  };

  const prevImage = () => {
    if (property && property.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  if (loading) {
    return (
      <div data-testid="loading-spinner" className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div data-testid="property-not-found" className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-lg text-muted-foreground">Bien non trouvé</div>
          <Link to="/properties">
            <Button>Retour aux biens</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="property-detail-page" className="min-h-screen pt-20">
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <Link to="/properties">
          <Button data-testid="back-button" variant="ghost" className="mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux biens
          </Button>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div 
              className="aspect-video overflow-hidden cursor-pointer group relative"
              onClick={() => openLightbox(0)}
              data-testid="main-property-image"
            >
              <img
                src={property.images[0] || 'https://images.unsplash.com/photo-1520608421741-68228b76b6df'}
                alt={property.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm uppercase tracking-widest">
                  Cliquez pour agrandir
                </span>
              </div>
            </div>

            {property.images.length > 1 && (
              <div className="grid grid-cols-3 gap-4">
                {property.images.slice(1, 4).map((img, idx) => (
                  <div 
                    key={idx}
                    className="cursor-pointer group relative overflow-hidden"
                    onClick={() => openLightbox(idx + 1)}
                    data-testid={`thumbnail-${idx + 1}`}
                  >
                    <img
                      src={img}
                      alt={`${property.title} - ${idx + 2}`}
                      className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  <MapPin className="w-3 h-3" />
                  {property.location}
                </div>
                <h1 data-testid="property-title" className="text-4xl md:text-5xl font-serif tracking-tight">{property.title}</h1>
              </div>

              <div className="flex items-center gap-8 py-6 border-y border-border">
                <div className="flex items-center gap-2">
                  <Bed className="w-5 h-5 text-primary" />
                  <span className="text-lg">{property.bedrooms} chambres</span>
                </div>
                <div className="flex items-center gap-2">
                  <Bath className="w-5 h-5 text-primary" />
                  <span className="text-lg">{property.bathrooms} salles de bain</span>
                </div>
                <div className="flex items-center gap-2">
                  <Square className="w-5 h-5 text-primary" />
                  <span className="text-lg">{property.surface}m²</span>
                </div>
                {property.peb && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">PEB</span>
                    <span className="text-lg font-semibold bg-primary/10 text-primary px-3 py-1 rounded">{property.peb}</span>
                  </div>
                )}
              </div>

              <div>
                <h2 className="text-2xl font-serif tracking-tight mb-4">Description</h2>
                <p data-testid="property-description" className="text-base leading-relaxed text-muted-foreground whitespace-pre-wrap">
                  {property.description}
                </p>
              </div>

              <div className="bg-secondary p-8 space-y-4">
                <div className="flex items-baseline gap-3">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Prix</div>
                  <div data-testid="property-price" className="text-4xl font-serif font-bold text-accent">€{property.price.toLocaleString()}</div>
                </div>
                <div className="text-sm text-muted-foreground">Type: {property.property_type}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <ContactForm propertyId={property.id} propertyTitle={property.title} />
            </div>
          </div>
        </div>
      </div>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-7xl h-[90vh] p-0 bg-black border-0">
          <div className="relative w-full h-full flex items-center justify-center">
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-50 text-white hover:text-primary transition-colors"
              data-testid="close-lightbox"
            >
              <X className="w-8 h-8" />
            </button>

            {property && property.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 z-50 text-white hover:text-primary transition-colors"
                  data-testid="prev-image"
                >
                  <ChevronLeft className="w-12 h-12" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 z-50 text-white hover:text-primary transition-colors"
                  data-testid="next-image"
                >
                  <ChevronRight className="w-12 h-12" />
                </button>
              </>
            )}

            <img
              src={property?.images[currentImageIndex]}
              alt={`${property?.title} - Image ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              data-testid="lightbox-image"
            />

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
              {currentImageIndex + 1} / {property?.images.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PropertyDetailPage;